// useUrlNavigation — the ONE impure seam that mirrors the frame into the URL and
// projects the URL back into the frame (F48). The sanctioned shell edge, like
// `useNow`: the routing itself (parseRoute / routeToUrl / routeOfFrame /
// routeToFrame) is pure and unit-tested; only the `pushState`/`popstate` calls
// and the id→title/id→kind lookups (which need loaded state) live here. The
// frame reducer stays the SINGLE writer of `FrameState` — this hook only reads
// from it (to push) and dispatches into it (on pop/boot).
//
// THE URL IS THE SOURCE OF TRUTH AT BOOT (F48 v2): on load the URL determines the
// view. The device-restored last-view (viewState) still restores the open TABS
// (working memory), but the ACTIVE FOCUS is driven by the URL — booting `/` lands
// on a NEW CHAT, never a reflected `/settings/…` or `/c/:id` from the last
// session. The forward mirror is gated on `booted` so nothing reflects the
// restored view into the bar before the URL has been applied.
//
// Booting `/` SUPERSEDES the restored active tab's focus with a fresh new chat
// (the `startNewChat` transition), rather than reusing whatever conversation was
// last active. This is deliberate: a restored DURABLE conversation would
// otherwise reflect `/c/:id` into the bar the moment its durability is known
// (conversation summaries load async, so it can't be told apart from a nascent
// at boot). A freshly-minted nascent is guaranteed non-durable, so it derives
// `/` and stays there — and because the user's first message lands IN that fresh
// chat, becoming durable there is a normal `/` → `/c/:id` promotion, never a
// yank. (Post-boot `/` navigation reuses an open nascent via `chatNav`, so it
// does not keep minting; a fresh device's cold-open seed conversation coexists
// as an extra empty tab in working memory — see the PR's residual note.)
//
// Two directions, both under a plain-string idempotence guard (not a
// re-entrancy flag): `history.pushState` never fires `popstate`, so a pop that
// drives the frame produces a derived URL already equal to `location`, and the
// forward effect skips — the equality IS the guard.
//   forward (state → URL): derive the route from the frame and pushState its
//     canonical URL when it differs from the bar's canonical URL.
//   backward (URL → state): on `popstate` (real Back/Forward, side-buttons,
//     swipe, Alt+Left), parse the new URL and apply it.
//
// Arms a pure function can't build are resolved HERE (coordinator's rule: state
// in the wiring, never in the pure bridge):
//   - `chat`: `chatNav()` (from the shell) lands on an open nascent conversation
//     or mints a fresh one — ids never in a pure fold.
//   - resources: `refFor(id)` resolves the loaded `ResourceRef` (title + kind);
//     a cold deep-link before the Library loads opens a title-less placeholder
//     that `reconcile-tabs` heals on load.
//   - `quality`: the material title comes from `refFor`.
//   - `notFound`: raises the 404 gate (`setNotFound`) instead of a transition;
//     while it is up the forward mirror is suppressed so the bar keeps the
//     unrecognized URL rather than bouncing to a real view.
//
// Nascent-vs-durable: a focused NASCENT conversation mirrors to `/` (new chat);
// a DURABLE one to `/c/:id`; while the conversation list isn't held the
// durability is UNKNOWN and the bar is left alone (D50). The durability signal
// is shell state, read here and handed to the pure `mirroredRoute`.

import { type Dispatch, useEffect, useRef, useState } from "react";
import type { ResourceRef } from "../../components/Navigation/types.js";
import type { Durability } from "../conversationSummary.js";
import type { FrameState, FrameTransition } from "../frame.js";
import {
    type HistoryMode,
    historyModeOf,
    mirroredRoute,
    routeOfFrame,
    routeToFrame,
} from "./bridge.js";
import { parseRoute } from "./parse.js";
import { routeToUrl } from "./serialize.js";
import type { Route } from "./types.js";

export type UrlNavDeps = {
    readonly frame: FrameState;
    readonly dispatch: Dispatch<FrameTransition>;
    // The loaded Library projection: id → its `ResourceRef` (title + kind), or
    // undefined before the Library loads / for an unknown id.
    readonly refFor: (id: string) => ResourceRef | undefined;
    // Whether a conversation id is DURABLE (has a server aggregate), NASCENT, or
    // not yet known (the list isn't held). Drives the nascent → `/` downgrade;
    // unknown holds the bar as it is (D50).
    readonly conversationDurability: (id: string) => Durability;
    // The transitions that REUSE an open nascent conversation (else mint one) —
    // for post-boot `/` navigation, when durability is known so a reuse is safe.
    // Computed by the shell (open-conversation set + minter); called fresh each
    // time so a mint is never reused.
    readonly chatNav: () => readonly FrameTransition[];
    // The transitions that ALWAYS mint a fresh nascent conversation — for boot
    // `/`, where durability is unknown so a fresh (guaranteed non-durable) chat
    // is the only safe way to supersede the restored active focus without a
    // wrong reuse. Fresh mint each call.
    readonly startNewChat: () => readonly FrameTransition[];
    // The 404 gate setter (shell state). `true` raises "Sidan finns inte".
    readonly notFound: boolean;
    readonly setNotFound: (notFound: boolean) => void;
};

// The address bar's CURRENT route, for the idempotence comparison (in
// canonical spelling) and the push-or-replace decision.
const currentRoute = (): Route => parseRoute(new URL(window.location.href));

// Write a route URL (path + route-owned query only) as a new history entry or
// over the current one (`historyModeOf`).
const writeRouteUrl = (routeUrl: string, mode: HistoryMode): void => {
    const url = new URL(routeUrl, window.location.origin);
    const path = `${url.pathname}${url.search}`;
    if (mode === "push") window.history.pushState(null, "", path);
    else window.history.replaceState(null, "", path);
};

// A title-less package ref for a cold deep-link (Library not yet loaded).
// `reconcile-tabs` heals the title once the Library lands (or drops the tab if
// the id is gone).
const placeholderPackageRef = (id: string): ResourceRef => ({
    type: "export",
    id,
    title: "",
});

// Interpret a parsed route into the frame, resolving the state-dependent arms.
const applyRoute = (deps: UrlNavDeps, route: Route): void => {
    switch (route.type) {
        case "chat":
            for (const t of deps.chatNav()) deps.dispatch(t);
            return;
        case "package":
            deps.dispatch({
                type: "open-resource",
                ref: deps.refFor(route.id) ?? placeholderPackageRef(route.id),
            });
            return;
        case "quality":
            deps.dispatch({
                type: "open-quality-report",
                materialId: route.materialId,
                title: deps.refFor(route.materialId)?.title ?? "",
            });
            return;
        case "library": {
            // A pre-#59 link to a source or file in the library
            // (`/library/{id}?filter=source`, or an id the loaded index names
            // a source) opens it in the Knowledge bank, its home now.
            const kind =
                route.selected === undefined
                    ? undefined
                    : (deps.refFor(route.selected)?.type ?? route.facet);
            if (kind === "source" || kind === "file") {
                deps.dispatch({
                    type: "open-knowledge",
                    selectedId: route.selected,
                });
                return;
            }
            for (const t of routeToFrame(route)) deps.dispatch(t);
            return;
        }
        default:
            // Journal / Library / Settings / Conversation — the pure arms.
            // (notFound never reaches here; applyUrl handles it.)
            for (const t of routeToFrame(route)) deps.dispatch(t);
    }
};

// Parse a URL and apply it: an unrecognized path raises the 404 gate; anything
// else lowers it and applies the route.
const applyUrl = (deps: UrlNavDeps, url: URL): void => {
    const route = parseRoute(url);
    if (route.type === "notFound") {
        deps.setNotFound(true);
        return;
    }
    deps.setNotFound(false);
    applyRoute(deps, route);
};

export const useUrlNavigation = (deps: UrlNavDeps): void => {
    const { frame, conversationDurability, notFound } = deps;

    // Latest deps for the once-registered boot/popstate listeners.
    const latest = useRef(deps);
    latest.current = deps;

    // The route the frame projects to, with the nascent → new-chat downgrade
    // and the hold while durability is unknown (`mirroredRoute`). Computed in
    // render so the forward effect keys on a stable string.
    const effective = mirroredRoute(
        routeOfFrame(frame),
        conversationDurability,
    );
    const derivedUrl = effective === null ? null : routeToUrl(effective);

    // Boot: the URL is authoritative. Apply it once, then open the forward gate.
    // `/` (chat) SUPERSEDES the restored active focus with a FRESH new chat (see
    // the header) — never a reuse of the restored active, which could be a
    // durable conversation. Every other route applies normally.
    const [booted, setBooted] = useState(false);
    useEffect(() => {
        if (typeof window !== "undefined") {
            const url = new URL(window.location.href);
            const route = parseRoute(url);
            if (route.type === "chat") {
                latest.current.setNotFound(false);
                for (const t of latest.current.startNewChat())
                    latest.current.dispatch(t);
            } else {
                applyUrl(latest.current, url);
            }
        }
        setBooted(true);
    }, []);

    // Backward: project a real browser navigation back into the frame.
    useEffect(() => {
        if (typeof window === "undefined") return;
        const onPopState = (): void =>
            applyUrl(latest.current, new URL(window.location.href));
        window.addEventListener("popstate", onPopState);
        return () => window.removeEventListener("popstate", onPopState);
    }, []);

    // Forward: mirror the frame's derived route into the bar, under the
    // string-equality guard. Gated until boot has applied the URL, and
    // suppressed while the 404 gate is up so the bar keeps the unknown URL.
    useEffect(() => {
        if (typeof window === "undefined") return;
        if (!booted || notFound) return;
        if (derivedUrl === null) return;
        const current = currentRoute();
        if (derivedUrl === routeToUrl(current)) return;
        const next = parseRoute(new URL(derivedUrl, window.location.origin));
        writeRouteUrl(derivedUrl, historyModeOf(current, next));
    }, [booted, notFound, derivedUrl]);
};
